import { UserX, Edit, Trash2, History } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard, EmployeeDetails } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { DialogModal, ConfirmPopupModal } from "../../components/ui/dialogModal";
import { Textarea } from "../../components/ui/input";
import { truncateText } from "../../components/common/CommonFunctions";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { cn } from "../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../components/ui/fieldStyles";
import {
    RESIGNATION_URL,
    RESIGNATION_APPROVER_URL,
} from "../../constants/api";

const StatusBadge = ({ status }) => {
    const config = {
        pending: "bg-yellow-100 text-yellow-700 border-yellow-200",
        approved: "bg-green-100 text-green-700 border-green-200",
        rejected: "bg-red-100 text-red-700 border-red-200",
        cancelled: "bg-gray-100 text-gray-600 border-gray-200",
    };
    const cls = config[status] || "bg-blue-100 text-blue-700 border-blue-200";
    const text = status ? status.charAt(0).toUpperCase() + status.slice(1) : "Pending";
    return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${cls}`}>
            {text}
        </span>
    );
};

const formatDate = (value, fmt = "DD/MM/YYYY") => {
    if (!value) return "-";
    const d = dayjs(value);
    return d.isValid() ? d.format(fmt) : "-";
};

const ResignationRequest = () => {
    const { fetch, create, update } = useApiMethods();

    const [tableData, setTableData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [loading, setLoading] = useState(false);

    // `extras.active` from the list endpoint marks whether an active (non-rejected,
    // non-finished) resignation exists for this user. When true, hide the "New" button
    // — the backend rejects a duplicate POST anyway, but the UI shouldn't invite it.
    const [hasActiveResignation, setHasActiveResignation] = useState(false);

    const [openDialog, setOpenDialog] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [editRecord, setEditRecord] = useState(null);

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteRecord, setDeleteRecord] = useState(null);

    const [showApprovalLog, setShowApprovalLog] = useState(false);
    const [selectedForLog, setSelectedForLog] = useState(null);

    const [approverList, setApproverList] = useState([]);
    const [finalApproverList, setFinalApproverList] = useState([]);

    // `fetch` from useApiMethods is a fresh function reference each render — pin it
    // in a ref so effects don't re-fire and loop the API call.
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;

    const [formState, setFormState] = useState({
        approved_by: undefined,
        final_approved_by: undefined,
        request_date: dayjs(),
        reason: "",
    });

    const resetForm = () => {
        setIsEdit(false);
        setEditRecord(null);
        setFormState({
            approved_by: undefined,
            final_approved_by: undefined,
            request_date: dayjs(),
            reason: "",
        });
    };

    const handleFormChange = (field, value) => {
        setFormState((prev) => ({ ...prev, [field]: value }));
    };

    // Approver dropdowns (mount-only)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const apiData = await fetchRef.current(RESIGNATION_APPROVER_URL, { drop_down: "True" });
                if (cancelled) return;
                if (apiData?.status === "success" && apiData.data) {
                    setApproverList(apiData.data.approver || []);
                    setFinalApproverList(apiData.data.final_approver || []);
                }
            } catch (error) {
                if (!cancelled) ToastMsg("error", error.message);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Side fetch to read `extras.active` — Reactable owns extras internally and
    // doesn't expose it, so we do a tiny separate request and re-run whenever the
    // table refreshes (after submit / delete).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const apiData = await fetchRef.current(RESIGNATION_URL, { page_no: 1, page_size: 1 });
                if (cancelled) return;
                if (apiData?.status === "success") {
                    setHasActiveResignation(apiData.data?.extras?.active === true);
                }
            } catch {
                // silent — the table fetch will surface real errors
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [serverTrigger]);

    const handleSubmitRequest = async () => {
        if (!formState.approved_by || !formState.final_approved_by || !formState.reason?.trim()) {
            ToastMsg("error", "Please fill in all required fields");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                approved_by: formState.approved_by,
                final_approved_by: formState.final_approved_by,
                request_date: dayjs(formState.request_date).format("YYYY-MM-DD"),
                reason: formState.reason,
            };

            if (isEdit && editRecord?.id) {
                payload.id = editRecord.id;
            }

            const response = isEdit
                ? await update(RESIGNATION_URL, payload)
                : await create(RESIGNATION_URL, payload);

            if (response?.status === "success") {
                ToastMsg("success", response.message || (isEdit ? "Resignation updated" : "Resignation submitted"));
                setOpenDialog(false);
                resetForm();
                setServerTrigger((v) => !v);
            } else {
                ToastMsg("error", response?.message || "Failed to submit resignation");
            }
        } catch (error) {
            ToastMsg("error", error.message || "Submission failed");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRequest = async () => {
        if (!deleteRecord) return;
        setLoading(true);
        try {
            const response = await update(RESIGNATION_URL, { id: deleteRecord.id, is_active: false });
            if (response?.status === "success") {
                ToastMsg("success", "Resignation request deleted");
                setShowDeleteConfirm(false);
                setDeleteRecord(null);
                setServerTrigger((v) => !v);
            } else {
                ToastMsg("error", response?.message || "Failed to delete request");
            }
        } catch (error) {
            ToastMsg("error", error.message || "Delete failed");
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            accessor: "request_date",
            Header: "Request Date",
            sortable: true,
            filterable: true,
            filterType: "date",
            width: "150px",
            Cell: ({ value }) => (
                <span className="text-gray-700 dark:text-gray-300">{formatDate(value)}</span>
            ),
        },
        {
            accessor: "reason",
            Header: "Reason",
            sortable: false,
            width: "260px",
            Cell: ({ value }) => (value ? truncateText(value, 30, "300px", "balance") : "-"),
        },
        {
            accessor: "notice_period",
            Header: "Notice Period",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="text-gray-700 dark:text-gray-300">
                    {value !== null && value !== undefined ? `${value} days` : "-"}
                </span>
            ),
        },
        {
            accessor: "last_working_day",
            Header: "Last Working Day",
            sortable: true,
            width: "160px",
            Cell: ({ value }) => (
                <span className="text-gray-700 dark:text-gray-300">{formatDate(value)}</span>
            ),
        },
        {
            accessor: "resignation_status",
            Header: "Status",
            sortable: true,
            filterable: true,
            filterType: "requestStatus",
            width: "130px",
            Cell: ({ value }) => <StatusBadge status={value} />,
        },
        {
            Header: "Actions",
            accessor: "actions",
            sortable: false,
            fixed: true,
            align: "right",
            width: "120px",
            Cell: ({ row }) => {
                const status = row.resignation_status || row.approval_status;
                const isPending = status === "pending";
                const hasLog = Array.isArray(row.approval_log) && row.approval_log.length > 0;

                return (
                    <div className="flex justify-end gap-2">
                        {isPending ? (
                            <>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => {
                                        setIsEdit(true);
                                        setEditRecord(row);
                                        setFormState({
                                            approved_by: row.approved_by || undefined,
                                            final_approved_by: row.final_approved_by || undefined,
                                            request_date: row.request_date ? dayjs(row.request_date) : dayjs(),
                                            reason: row.reason || "",
                                        });
                                        setOpenDialog(true);
                                    }}
                                    className="h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 hover:text-blue-700"
                                    title="Edit Resignation"
                                >
                                    <Edit className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => {
                                        setDeleteRecord(row);
                                        setShowDeleteConfirm(true);
                                    }}
                                    className="h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 hover:text-red-700"
                                    title="Delete Resignation"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </>
                        ) : (
                            <Button
                                variant="ghost"
                                size="xs"
                                onClick={() => {
                                    setSelectedForLog(row);
                                    setShowApprovalLog(true);
                                }}
                                disabled={!hasLog}
                                className={`h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 ${
                                    hasLog
                                        ? "bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100 hover:text-amber-700"
                                        : "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                                }`}
                                title={hasLog ? "View Approval Log" : "No approval log available"}
                            >
                                <History className="h-3.5 w-3.5" />
                            </Button>
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={UserX}
                title="Resignation Request"
                subtitle="Submit and track your resignation"
                totalRecords={totalRecords}
                buttonEnable={!hasActiveResignation}
                setOpenDialog={(open) => {
                    if (open) resetForm();
                    setOpenDialog(open);
                }}
                buttonName="New Resignation Request"
            />

            <Reactable
                columns={columns}
                data={tableData}
                setData={setTableData}
                setTotalCount={setTotalRecords}
                serverSide
                url={RESIGNATION_URL}
                serverTrigger={serverTrigger}
                rowSize="md"
                isCSVRequired
                isPDFRequired
            />

            {/* Create / Edit Dialog */}
            <DialogModal
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    resetForm();
                }}
                onSave={handleSubmitRequest}
                title={isEdit ? "Edit Resignation Request" : "Submit Resignation Request"}
                saveLabel={isEdit ? "Update" : "Submit"}
                width="600px"
                loading={loading}
            >
                <div className="space-y-4 p-1">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>
                                Requested Date
                            </label>
                            <DatePicker
                                value={formState.request_date}
                                disabled
                                format="DD-MM-YYYY"
                                className="w-full"
                                size="large"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>
                                Approver <span className="text-red-500">*</span>
                            </label>
                            <Select
                                value={formState.approved_by}
                                onChange={(value) => handleFormChange("approved_by", value)}
                                placeholder="Select approver"
                                className="w-full"
                                size="large"
                                disabled={isEdit}
                                showSearch
                                optionFilterProp="children"
                            >
                                {approverList.map((opt) => (
                                    <Select.Option key={opt.id} value={opt.id}>
                                        {opt.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className={modalFieldLabelClass}>
                            Final Approver <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formState.final_approved_by}
                            onChange={(value) => handleFormChange("final_approved_by", value)}
                            placeholder="Select final approver"
                            className="w-full"
                            size="large"
                            disabled={isEdit}
                            showSearch
                            optionFilterProp="children"
                        >
                            {finalApproverList.map((opt) => (
                                <Select.Option key={opt.id} value={opt.id}>
                                    {opt.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <label className={modalFieldLabelClass}>
                            Reason for Resignation <span className="text-red-500">*</span>
                        </label>
                        <Textarea
                            value={formState.reason}
                            onChange={(e) => handleFormChange("reason", e.target.value)}
                            placeholder="Please provide a reason for your resignation..."
                            rows={5}
                            showCharCount
                            maxLength={150}
                            className="w-full"
                        />
                    </div>

                    <div className={cn(
                        "p-3 rounded-lg border text-xs",
                        "bg-blue-50 border-blue-200 text-blue-700",
                        "dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300"
                    )}>
                        Once submitted, this request is routed to your approver and then to the final approver.
                        You will not be able to edit it after the first approval.
                    </div>
                </div>
            </DialogModal>

            {/* Approval Log Dialog */}
            <DialogModal
                open={showApprovalLog}
                onClose={() => {
                    setShowApprovalLog(false);
                    setSelectedForLog(null);
                }}
                title="Approval Log"
                showActions={false}
                width="800px"
            >
                {selectedForLog && Array.isArray(selectedForLog.approval_log) && selectedForLog.approval_log.length > 0 ? (
                    <div className="space-y-4">
                        <div className="overflow-x-auto max-h-64 relative">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {selectedForLog.approval_log.map((log, index) => (
                                        <tr key={log.id ?? index} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                Level {log.level ?? "-"}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                {log.approval_date ? dayjs(log.approval_date).format("DD/MM/YYYY") : "-"}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <StatusBadge status={log.approval_status} />
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                {log.approval_comments || "-"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="text-sm flex items-center justify-between gap-2 text-gray-600 border-t pt-4">
                            {(() => {
                                const rf = selectedForLog.request_from || selectedForLog.employee_details;
                                if (!rf) return <span />;
                                const empDetails = {
                                    ...rf,
                                    designation_id: rf?.designation
                                        ? { id: rf.designation.id, designation_name: rf.designation.name }
                                        : { id: 0, designation_name: "" },
                                    first_name: rf.employee_name || "-",
                                    id: rf.employee_id || "-",
                                };
                                return <EmployeeDetails details={empDetails} size="xs" />;
                            })()}
                            <p>
                                <strong>Current Status:</strong>{" "}
                                <StatusBadge status={selectedForLog.resignation_status || selectedForLog.approval_status} />
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <p className="text-gray-500">No approval log available for this request.</p>
                    </div>
                )}
            </DialogModal>

            {/* Delete Confirmation */}
            <ConfirmPopupModal
                open={showDeleteConfirm}
                onClose={() => {
                    setShowDeleteConfirm(false);
                    setDeleteRecord(null);
                }}
                onConfirm={handleDeleteRequest}
                title="Delete Resignation Request"
                description={
                    <span>
                        Are you sure you want to delete this resignation request? This action cannot be undone.
                    </span>
                }
                type="danger"
                destructive
                confirmLabel="Delete"
                loading={loading}
            />
        </ContainerCard>
    );
};

export default ResignationRequest;
